import { DifficultyLevel } from '@opositandopn/ui';

import { DifficultyLevel as DeclaredDifficultyLevel } from '../questions/api/useMultipleChoiceQuestions';
import { AssessmentMetricsViewModel } from './api/useMultipleChoiceAssessments';

/**
 * Where the field's average mark stops reading as an easy simulacro and starts reading
 * as an ordinary one, and where an ordinary one turns hard. Marks are on the 0–10 scale
 * every other figure on the card is read on, so the bands are the ones the reader
 * already grades themselves against: a pass at five, a notable at seven.
 */
const EASY_AVERAGE_GRADE = 7;
const MEDIUM_AVERAGE_GRADE = 5;

/**
 * How many sittings the field needs before it is allowed to overrule the level the
 * assessment declares. A handful of marks say more about who happened to sit it first
 * than about the simulacro, and a badge that swings on one more attempt is worse than
 * one that never moves.
 *
 * Read against the count the API reports, which is the true one only up to fifteen and
 * inflated threefold above it — so this must stay at or under fifteen to mean the number
 * of people it reads as. Past that the bar would land at a third of whatever is written
 * here, and the figures in between could not be asked for at all.
 */
const MIN_FIELD_SIZE = 10;

/** The level the assessment declares, in the terms the card renders it in. */
function toDifficultyLevel(declared: DeclaredDifficultyLevel): DifficultyLevel {
  return declared === 'easy'
    ? DifficultyLevel.EASY
    : declared === 'medium'
      ? DifficultyLevel.MEDIUM
      : DifficultyLevel.HARD;
}

/**
 * How hard the simulacro turned out to be, read off the marks it has actually produced.
 * Undefined until enough people have sat it for the average to mean anything — an
 * assessment nobody has finished comes back with its figures at nought, which would
 * otherwise read as the hardest simulacro on the platform. The count is answered whether
 * or not the reader is on the board themselves, so the badge no longer waits for them to
 * sit the simulacro before it can read it.
 */
function difficultyFromMetrics(
  metrics: AssessmentMetricsViewModel,
): DifficultyLevel | undefined {
  if (metrics.total < MIN_FIELD_SIZE) {
    return undefined;
  }

  if (metrics.averageGrade >= EASY_AVERAGE_GRADE) {
    return DifficultyLevel.EASY;
  }

  return metrics.averageGrade >= MEDIUM_AVERAGE_GRADE
    ? DifficultyLevel.MEDIUM
    : DifficultyLevel.HARD;
}

/**
 * The difficulty a simulacro is shown with: what the field made of it where there is a
 * field to read, and the level the assessment was published with until then. The
 * declared level is an author's estimate, so a large enough set of real marks is the
 * better answer wherever one exists.
 *
 * Undefined while the metrics are still on their way, which is what keeps the badge
 * from being written twice: the assessment itself arrives first, and a level read off
 * it alone would be corrected under the reader's eyes a moment later. Answering with
 * nothing until both are in hand shows one level, once — the card leaves the badge off
 * meanwhile, rather than showing a figure it is about to take back.
 *
 * @param declared The level the assessment was published with.
 * @param metrics The field, once it has arrived.
 * @param metricsPending Whether the metrics are still being fetched.
 */
function resolveDifficulty(
  declared: DeclaredDifficultyLevel,
  metrics?: AssessmentMetricsViewModel,
  metricsPending = false,
): DifficultyLevel | undefined {
  if (metricsPending) {
    return undefined;
  }

  return (
    (metrics && difficultyFromMetrics(metrics)) ?? toDifficultyLevel(declared)
  );
}

export { resolveDifficulty };
